/*
 * certificate-maker/pdf.js — the same primitives, on paper.
 *
 * WHY A PDF AND NOT window.print()
 * ────────────────────────────────
 * An HTML page has no physical size. Printing one passes through the page box,
 * the @page rule, the browser's margins and the driver's "fit to page", and any
 * of those can resize it. A PDF page box is absolute — 1 pt is 1/72 inch by
 * definition — so a title set at 20 mm is 20 mm in the file. What is left is the
 * driver, which is why `rulerMm` can print a measured bar: the user can PROVE
 * the scale after printing instead of trusting a checkbox.
 *
 * WHY THE TEXT IS TEXT
 * ────────────────────
 * Every competitor surveyed on 2026-08-23 hands back either a raster image or a
 * PDF with a full-page JPEG inside it. That loses three things at once: the type
 * is resolution-bound (a 96 dpi ornament on A4 turns to mush), the file is
 * megabytes, and the recipient's name is not selectable, searchable or
 * correctable. Here the glyphs are real glyphs from a subset-embedded face and
 * the ornament is real vector, so a whole class of 30 certificates is a few
 * hundred kilobytes and every one of them is sharp at any zoom.
 *
 * WHY A BATCH IS ONE DOCUMENT
 * ───────────────────────────
 * Fonts are embedded once per PDF, not once per page. Thirty pages of a class
 * list therefore cost thirty pages of glyph positions plus ONE copy of each
 * face — the difference between ~0.3 MB and ~9 MB, and the reason the batch
 * default is a single multi-page PDF rather than a ZIP of thirty files.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertPdf = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var PT = 72 / 25.4;              // exact: points per millimetre
    var _lib = null;

    /* pdf-lib IS the export. cdn_chain() puts OUR origin first on every locale
     * and an external mirror second — a hard-coded '/vendor/…' is right for RU
     * by accident, and cdn() picks by LOCALE, which would send a visitor in
     * Russia reading the English page to jsdelivr. Unreachable there is a dead
     * button, not a slow one. */
    function lib() {
        if (_lib) return Promise.resolve(_lib);
        if (window.PDFLib) { _lib = window.PDFLib; return Promise.resolve(_lib); }
        var cfg = (window.__CERT__ && window.__CERT__.assets) || {};
        var urls = cfg.pdfLib && cfg.pdfLib.length ? cfg.pdfLib : ['/vendor/pdf-lib/pdf-lib.min.js'];
        var p = (window.__assetChain && window.__assetChain.loadScript)
            ? window.__assetChain.loadScript(urls, 'pdf-lib')
            : new Promise(function (res, rej) {
                var s = document.createElement('script');
                s.src = urls[0]; s.async = true;
                s.onload = res; s.onerror = function () { rej(new Error('pdf-lib failed to load')); };
                document.head.appendChild(s);
            });
        return p.then(function () {
            if (!window.PDFLib) throw new Error('pdf-lib failed to load');
            _lib = window.PDFLib;
            return _lib;
        });
    }

    function rgb(hexStr, PDFLib) {
        var h = String(hexStr || '#000').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return PDFLib.rgb(
            (parseInt(h.slice(0, 2), 16) || 0) / 255,
            (parseInt(h.slice(2, 4), 16) || 0) / 255,
            (parseInt(h.slice(4, 6), 16) || 0) / 255
        );
    }

    /* ── fonts ───────────────────────────────────────────────────────────────
     * One cache per PDFDocument. `PdfTextFont.embed` subsets, so a page of
     * Cyrillic costs ~8 KB of face rather than ~300 KB, and asks fontkit for
     * the real cmap rather than trusting a name. */
    function fontCache(pdfDoc) {
        var cache = {};
        return function (faceId, bold) {
            var key = faceId + (bold ? '|b' : '');
            if (cache[key]) return cache[key];
            cache[key] = window.PdfTextFont.embed(pdfDoc, faceId, { bold: !!bold, subset: true })
                .catch(function (e) {
                    /* A face that will not embed must not kill the export: fall
                     * back to the one face we know covers Cyrillic and Latin and
                     * tell the caller, rather than handing back a blank page. */
                    if (faceId === 'noto' && !bold) throw e;
                    return window.PdfTextFont.embed(pdfDoc, 'noto', { bold: !!bold, subset: true });
                });
            return cache[key];
        };
    }

    /* Runs of one face, so «Диплом 수료증» sets in two faces on one line instead
     * of one face and a row of blank boxes. */
    function planRuns(text, preferId) {
        if (window.PdfTextFont && window.PdfTextFont.planRuns) {
            var runs = window.PdfTextFont.planRuns(text, preferId);
            return runs.map(function (r) { return { text: r.text, faceId: r.faceId || 'noto' }; });
        }
        return [{ text: text, faceId: preferId || 'noto' }];
    }

    function runWidth(run, font, sizePt, lsPt) {
        var w = font.widthOfTextAtSize(run, sizePt);
        return lsPt ? w + lsPt * Math.max(0, run.length - 1) : w;
    }
    /* ── right-to-left: which way each PIECE of a line runs ──────────────────
     *
     * Lives in `pdf-text-font.js` — the same rule is needed by label-maker and
     * business-card, which draw text through the same fontkit layout and
     * carried the identical defect. It was measured here first (the numbers and
     * the four rejected candidates are in that file's header); keeping a second
     * copy here would mean the next fix lands in one of three places. */
    function bidiRuns(str) { return window.PdfTextFont.bidiRuns(str); }
    function runText(run) { return window.PdfTextFont.runText(run); }

    /* ── drawing one page ────────────────────────────────────────────────── */

    function drawPage(page, prims, ctx) {
        var PDFLib = ctx.PDFLib, H = ctx.pageH;
        var chain = Promise.resolve();

        function Y(mm) { return (H - mm) * PT; }
        function X(mm) { return mm * PT; }

        prims.forEach(function (p) {
            if (p.type === 'rect') {
                page.drawRectangle({
                    x: X(p.x), y: Y(p.y + p.h), width: p.w * PT, height: p.h * PT,
                    color: p.fill && p.fill !== 'none' ? rgb(p.fill, PDFLib) : undefined,
                    borderColor: p.color ? rgb(p.color, PDFLib) : undefined,
                    borderWidth: (p.sw || 0) * PT,
                    opacity: p.opacity == null ? undefined : p.opacity
                });
                return;
            }
            if (p.type === 'circle') {
                page.drawCircle({
                    x: X(p.cx), y: Y(p.cy), size: p.r * PT,
                    color: p.fill && p.fill !== 'none' ? rgb(p.fill, PDFLib) : undefined,
                    borderColor: p.color ? rgb(p.color, PDFLib) : undefined,
                    borderWidth: (p.w || 0) * PT,
                    opacity: p.opacity == null ? undefined : p.opacity,
                    borderOpacity: p.opacity == null ? undefined : p.opacity
                });
                return;
            }
            if (p.type === 'path') {
                /* The 'd' is in millimetres with y down; drawSvgPath is also
                 * y-down from the given origin, so the origin is the page's TOP
                 * left and the scale converts mm → pt. One conversion, one place.
                 * ⚠️ Which is exactly why the stroke width and the dash lengths
                 * stay in MILLIMETRES: pdf-lib emits its `cm` BEFORE the
                 * line-width and dash operators, so both ride that same scale.
                 * The rect and circle branches above go through drawRectangle /
                 * drawCircle, which take POINTS — two conventions in one file,
                 * and this branch had the wrong one. A frame drawn at 0.3 mm
                 * printed at 0.85 mm, and its dashes 2.83× too long. */
                window.__pdfSvgPath(page, p.d, {
                    x: 0, y: H * PT, scale: PT,
                    borderColor: p.color ? rgb(p.color, PDFLib) : undefined,
                    borderWidth: (p.w || 0),
                    color: p.fill && p.fill !== 'none' ? rgb(p.fill, PDFLib) : undefined,
                    opacity: p.opacity == null ? undefined : p.opacity,
                    borderOpacity: p.opacity == null ? undefined : p.opacity,
                    borderDashArray: p.dash ? p.dash.slice() : undefined
                });
                return;
            }
            if (p.type === 'image') {
                chain = chain.then(function () {
                    return ctx.image(p.src).then(function (img) {
                        if (!img) return;
                        /* Fit inside the box, keeping the aspect — a stretched
                         * logo is the single most common thing that makes a
                         * generated certificate look homemade. */
                        var ar = img.width / img.height;
                        var w = p.w, h = p.h;
                        if (w / h > ar) w = h * ar; else h = w / ar;
                        var cx = p.x + p.w / 2, cy = p.y + p.h / 2;
                        page.drawImage(img, {
                            x: X(cx - w / 2), y: Y(cy + h / 2), width: w * PT, height: h * PT,
                            opacity: p.opacity == null ? undefined : p.opacity,
                            rotate: p.rot ? PDFLib.degrees(-p.rot) : undefined
                        });
                    });
                });
                return;
            }
            if (p.type === 'text') {
                chain = chain.then(function () { return drawText(page, p, ctx, X, Y); });
            }
        });

        return chain;
    }

    function drawText(page, p, ctx, X, Y) {
        var sizePt = p.size * PT;
        var lsPt = (p.letterSpacing || 0) * PT;
        /* Direction first, face second — composed in PdfTextFont so that the
           three exporters that need it cannot disagree about the order. */
        var runs = window.PdfTextFont.visualRuns(p.s, p.font).map(function (r) {
            return { text: r.text, faceId: r.faceId || 'noto', rtl: r.rtl };
        });

        return Promise.all(runs.map(function (r) {
            return ctx.font(r.faceId, p.bold).then(function (f) { return { run: r, font: f }; });
        })).then(function (loaded) {
            var total = 0;
            loaded.forEach(function (L) { total += runWidth(L.run.text, L.font, sizePt, lsPt); });
            /* Letter spacing adds a gap after every glyph but the last, so the
             * ink width is total − ls. render.js compensates the same way; the
             * two agree because the rule is written down, not re-derived. */
            var ink = total;
            var x = X(p.x);
            if (p.anchor === 'middle') x -= ink / 2;
            else if (p.anchor === 'end') x -= ink;

            var col = rgb(p.color, ctx.PDFLib);
            loaded.forEach(function (L) {
                /* ⚠️ A right-to-left run is NEVER drawn glyph by glyph. Arabic
                 * letters take their shape from their neighbours, and one
                 * drawText per character asks the shaper to shape each letter
                 * alone — every one comes out in its isolated form and the word
                 * falls apart. Tracking goes through the PDF's own character
                 * spacing instead, which keeps the joins AND the width the
                 * layout engine measured. */
                if (L.run.rtl && lsPt) {
                    page.setFont(L.font);
                    var enc = L.font.encodeText(L.run.text);
                    page.pushOperators(
                        ctx.PDFLib.pushGraphicsState(),
                        ctx.PDFLib.beginText(),
                        ctx.PDFLib.setFillingColor(col),
                        ctx.PDFLib.setFontAndSize(page.fontKey, sizePt),
                        ctx.PDFLib.setCharacterSpacing(lsPt),
                        ctx.PDFLib.setTextMatrix(1, 0, 0, 1, x, Y(p.y)),
                        ctx.PDFLib.showText(enc),
                        ctx.PDFLib.endText(),
                        ctx.PDFLib.popGraphicsState()
                    );
                    x += runWidth(L.run.text, L.font, sizePt, lsPt);
                    return;
                }
                if (!lsPt) {
                    page.drawText(L.run.text, {
                        x: x, y: Y(p.y), size: sizePt, font: L.font, color: col,
                        opacity: p.opacity == null ? undefined : p.opacity
                    });
                    x += L.font.widthOfTextAtSize(L.run.text, sizePt);
                    return;
                }
                /* pdf-lib has no character-spacing parameter, so a letter-spaced
                 * title is drawn glyph by glyph. That is also what makes the
                 * spacing EXACTLY the value on screen rather than the driver's
                 * idea of it. */
                var chars = Array.from(L.run.text);
                chars.forEach(function (ch) {
                    page.drawText(ch, {
                        x: x, y: Y(p.y), size: sizePt, font: L.font, color: col,
                        opacity: p.opacity == null ? undefined : p.opacity
                    });
                    x += L.font.widthOfTextAtSize(ch, sizePt) + lsPt;
                });
            });
        });
    }

    /* ── printer's marks ─────────────────────────────────────────────────── */

    function marksFor(w, h, opts) {
        var out = [];
        if (opts.crop) {
            /* Trim marks sit OUTSIDE the trim box in the bleed, which is the
             * only place a print shop can use them. With no bleed they would
             * land on the artwork, so they are drawn only when there is one. */
            var b = opts.bleed || 0, L = Math.min(5, Math.max(2, b));
            if (b > 0) {
                [[b, b, -1, -1], [w - b, b, 1, -1], [b, h - b, -1, 1], [w - b, h - b, 1, 1]].forEach(function (c) {
                    out.push({ type: 'path', d: 'M' + (c[0] + c[2] * 0.8) + ' ' + c[1] + 'L' + (c[0] + c[2] * (0.8 + L)) + ' ' + c[1], color: '#000', w: 0.12, fill: null });
                    out.push({ type: 'path', d: 'M' + c[0] + ' ' + (c[1] + c[3] * 0.8) + 'L' + c[0] + ' ' + (c[1] + c[3] * (0.8 + L)) + '', color: '#000', w: 0.12, fill: null });
                });
            }
        }
        if (opts.rulerMm) {
            /* A measured bar the user can put a ruler against. It is the only
             * honest way to answer "did my printer scale it?", and it is why
             * this tool can promise millimetres at all. */
            var mm = opts.rulerMm, y = h - 5, x0 = (w - mm) / 2;
            out.push({ type: 'path', d: 'M' + x0 + ' ' + y + 'h' + mm, color: '#888', w: 0.2, fill: null });
            out.push({ type: 'path', d: 'M' + x0 + ' ' + (y - 1.4) + 'v2.8M' + (x0 + mm) + ' ' + (y - 1.4) + 'v2.8', color: '#888', w: 0.2, fill: null });
        }
        return out;
    }

    /* ── public: build a PDF from N laid-out pages ───────────────────────── */

    /**
     * make(pages, opts) → Uint8Array
     *   pages  — [{ prims, page:{w,h} }] as produced by CertLayout.build
     *   opts   — { bleed, crop, rulerMm, scale, dx, dy, title, author }
     *
     * `scale/dx/dy` is the printer calibration: a driver that shrinks the sheet
     * by 1.6 % is common, and correcting it once beats reprinting a class set.
     */
    function make(pages, opts) {
        opts = opts || {};
        /* The coverage table has to be in memory BEFORE the first embed: without
         * it `faceById` knows nothing and every face is "unknown", which is the
         * export dying on its first line of text rather than falling back to
         * anything. `ready(text)` also pulls the CJK table when the document
         * actually contains CJK, so a Russian certificate never pays for it.
         * (Caught on the live page 2026-08-23: "unknown face: noto".) */
        var allText = pages.map(function (pg) {
            return pg.prims.filter(function (p) { return p.type === 'text'; })
                .map(function (p) { return p.s; }).join('');
        }).join('');
        var pre = (window.PdfTextFont && window.PdfTextFont.ready)
            ? window.PdfTextFont.ready(allText).catch(function () {})
            : Promise.resolve();
        return pre.then(lib).then(function (PDFLib) {
            return PDFLib.PDFDocument.create().then(function (doc) {
                doc.setTitle(opts.title || 'Certificate');
                doc.setProducer('Timbrica');
                doc.setCreator('Timbrica certificate-maker');
                var font = fontCache(doc);
                var imgCache = {};
                function image(src) {
                    if (imgCache[src]) return imgCache[src];
                    imgCache[src] = fetch(src).then(function (r) { return r.arrayBuffer(); }).then(function (buf) {
                        var head = new Uint8Array(buf.slice(0, 4));
                        var isPng = head[0] === 0x89 && head[1] === 0x50;
                        return isPng ? doc.embedPng(buf) : doc.embedJpg(buf);
                    }).catch(function () { return null; });
                    return imgCache[src];
                }

                var bleed = opts.bleed || 0;
                var chain = Promise.resolve();
                pages.forEach(function (pg) {
                    chain = chain.then(function () {
                        var w = pg.page.w + bleed * 2, h = pg.page.h + bleed * 2;
                        var page = doc.addPage([w * PT, h * PT]);
                        var prims = pg.prims;
                        /* Bleed and calibration are the same operation — move
                         * and scale the whole sheet — so they are applied once,
                         * here, rather than smeared through the layout. */
                        var k = opts.scale ? 1 + opts.scale / 100 : 1;
                        if (bleed || k !== 1 || opts.dx || opts.dy) {
                            prims = prims.map(function (p) { return shift(p, bleed + (opts.dx || 0), bleed + (opts.dy || 0), k, pg.page); });
                        }
                        prims = prims.concat(marksFor(w, h, { crop: opts.crop, bleed: bleed, rulerMm: opts.rulerMm }));
                        return drawPage(page, prims, { PDFLib: PDFLib, pageH: h, font: font, image: image });
                    });
                });

                return chain.then(function () { return doc.save({ useObjectStreams: true }); });
            });
        });
    }

    /* Scale about the page centre, then translate. Scaling about the origin
     * would move the artwork toward a corner, which is not what "my printer
     * shrinks by 1.6 %" means. */
    function shift(p, dx, dy, k, page) {
        var cx = page.w / 2, cy = page.h / 2;
        function fx(v) { return cx + (v - cx) * k + dx; }
        function fy(v) { return cy + (v - cy) * k + dy; }
        var o = {};
        for (var key in p) if (Object.prototype.hasOwnProperty.call(p, key)) o[key] = p[key];
        if (p.type === 'rect') { o.x = fx(p.x); o.y = fy(p.y); o.w = p.w * k; o.h = p.h * k; o.sw = (p.sw || 0) * k; }
        else if (p.type === 'circle') { o.cx = fx(p.cx); o.cy = fy(p.cy); o.r = p.r * k; o.w = (p.w || 0) * k; }
        else if (p.type === 'image') { o.x = fx(p.x); o.y = fy(p.y); o.w = p.w * k; o.h = p.h * k; }
        else if (p.type === 'text') { o.x = fx(p.x); o.y = fy(p.y); o.size = p.size * k; o.letterSpacing = (p.letterSpacing || 0) * k; o.w = (p.w || 0) * k; }
        else if (p.type === 'path') { o.d = scalePath(p.d, cx, cy, k, dx, dy); o.w = (p.w || 0) * k; }
        return o;
    }

    /* The path grammar this file emits is M/L/C/Z plus the h/v/Z the QR uses —
     * absolute pairs and two relative one-axis moves. Anything else would need
     * a real parser, and emitting anything else is a bug in frames.js. */
    function scalePath(d, cx, cy, k, dx, dy) {
        return d.replace(/([MLCmlchvV])([^MLCZmlczhvV]*)/g, function (_, cmd, args) {
            var nums = args.trim().length ? args.trim().split(/[\s,]+/).map(Number) : [];
            if (cmd === 'h') return 'h' + nums.map(function (v) { return r2(v * k); }).join(' ');
            if (cmd === 'v') return 'v' + nums.map(function (v) { return r2(v * k); }).join(' ');
            var out = [];
            for (var i = 0; i < nums.length; i += 2) {
                out.push(r2(cx + (nums[i] - cx) * k + dx), r2(cy + (nums[i + 1] - cy) * k + dy));
            }
            return cmd + out.join(' ');
        });
    }
    function r2(v) { return Math.round(v * 100) / 100; }

    return { make: make, lib: lib, _scalePath: scalePath, _bidiRuns: bidiRuns, _runText: runText, PT: PT };
});
